// Geocoding service to convert city, state, country to coordinates
// Uses Nominatim (OpenStreetMap) free geocoding API

interface GeocodeResult {
  lat: number;
  lng: number;
}

interface NominatimResponse {
  lat: string;
  lon: string;
  display_name: string;
}

// Cache to avoid repeated API calls for the same location
const geocodeCache = new Map<string, GeocodeResult>();

/**
 * Geocode a location (city, state, country) to coordinates
 * Results are cached to minimize API calls
 */
export async function geocodeLocation(
  city: string,
  state?: string,
  country?: string
): Promise<GeocodeResult | null> {
  // Guard against null/empty city
  if (!city || !city.trim()) {
    return null;
  }

  // Build cache key
  const cacheKey = `${city}|${state || ''}|${country || ''}`.toLowerCase();
  
  // Check cache first
  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey)!;
  }

  // Build search query
  const parts = [city];
  if (state) parts.push(state);
  if (country) parts.push(country);
  const searchQuery = parts.join(', ');

  try {
    // Use Nominatim free geocoding service
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=1`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'BowlingLeagueApp/1.0' // Nominatim requires a user agent
      }
    });

    if (!response.ok) {
      console.error('Geocoding failed:', response.status);
      return null;
    }

    const data: NominatimResponse[] = await response.json();

    if (data.length === 0) {
      console.warn(`No geocoding results for: ${searchQuery}`);
      return null;
    }

    const result: GeocodeResult = {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon)
    };

    // Cache the result
    geocodeCache.set(cacheKey, result);

    // Add small delay to respect Nominatim's usage policy (max 1 request per second)
    await new Promise(resolve => setTimeout(resolve, 1000));

    return result;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}

/**
 * Batch geocode multiple locations with rate limiting
 */
export async function geocodeLocations(
  locations: Array<{ city: string; state?: string; country?: string }>
): Promise<Map<string, GeocodeResult>> {
  const results = new Map<string, GeocodeResult>();

  for (const location of locations) {
    const cacheKey = `${location.city}|${location.state || ''}|${location.country || ''}`.toLowerCase();
    
    // Skip if already cached
    if (geocodeCache.has(cacheKey)) {
      results.set(cacheKey, geocodeCache.get(cacheKey)!);
      continue;
    }

    const result = await geocodeLocation(location.city, location.state, location.country);
    if (result) {
      results.set(cacheKey, result);
    }
  }

  return results;
}

/**
 * Clear the geocoding cache
 */
export function clearGeocodeCache(): void {
  geocodeCache.clear();
}
